import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Service, Signal, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay } from 'rxjs';

import { environment } from '../../../environments/environment';
import { MyProfileService } from '../profile/my-profile.service';
import { CsrfTokenStore } from './csrf-token.store';
import { CustomerProfileService } from './customer-profile.service';
import {
  classifyRefreshOutcome,
  REFRESH_THROTTLE_COOLDOWN_MS,
  RefreshOutcome,
} from './refresh-outcome';

/**
 * Every customer-auth endpoint lives under this prefix. The interceptor uses the
 * same constant to keep `/customer-auth/*` out of its 401 retry (a refresh that
 * answers 401 must not trigger another refresh).
 */
export const CUSTOMER_AUTH_URL_PREFIX = `${environment.apiUrl}/customer-auth/`;

export type CustomerSessionStatus = 'unknown' | 'authenticated' | 'anonymous';

export interface CustomerCredentials {
  email: string;
  password: string;
}

/**
 * The login/refresh response body carries exactly one field (`{ csrfToken }`) —
 * never the customer record. Treated as `unknown` and narrowed here rather than
 * cast, because it crosses the API boundary.
 */
function readCsrfToken(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const token = (body as Record<string, unknown>)['csrfToken'];

  return typeof token === 'string' && token.length > 0 ? token : null;
}

/**
 * Single source of truth about the customer session on the front.
 *
 * It holds no identity logic whatsoever: who the caller is, whether the account
 * is blocked and whether the e-mail is verified are decided by the backend, and
 * this service only records the HTTP verdict. Both JWTs live in HttpOnly cookies
 * the browser attaches itself; the only thing kept in memory is the CSRF token,
 * which arrives in the response body (see `CsrfTokenStore` — deliberately not in
 * localStorage/sessionStorage).
 */
@Service()
export class CustomerSessionService {
  private readonly http = inject(HttpClient);
  private readonly csrfTokenStore = inject(CsrfTokenStore);
  // Injected for `reset()` ONLY — this service never reads a profile, so no
  // request is made from here and the two caches keep their own owners.
  private readonly myProfile = inject(MyProfileService);
  private readonly customerProfile = inject(CustomerProfileService);

  private readonly currentStatus = signal<CustomerSessionStatus>('unknown');

  /** `unknown` until the first refresh settles it — the app starts without knowing. */
  readonly status = this.currentStatus.asReadonly();

  readonly isAuthenticated = computed(() => this.currentStatus() === 'authenticated');

  private readonly refreshThrottled = signal(false);

  /**
   * True while the panel is sitting out a 429 from `/refresh`. Its scope is the
   * service instance, i.e. the tab: a reload (F5) or a new tab starts with a clean
   * cooldown and pays one refresh. Deliberately NOT mirrored into
   * sessionStorage/localStorage/BroadcastChannel — that would add state to clean up
   * without helping the case this exists for (one office behind a single NAT, on
   * several machines).
   */
  readonly isRefreshThrottled: Signal<boolean> = this.refreshThrottled.asReadonly();

  private throttleTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Two rules stand here, and `refresh()` applies them in this order — cooldown
   * first, single flight second. (1) Inside the throttle window no `/refresh` is
   * sent at all: after a 429 the panel answers from memory, so a customer walking
   * back into `/panel` does not spend another slot of the backend's counter.
   * (2) Outside that window, parallel 401s (and a guard racing the interceptor)
   * share ONE call: the endpoint rotates the CSRF token, so a second concurrent
   * refresh would invalidate the token the first one just stored. The suppressed
   * stream never lands here — it would clear the handle of a request in flight.
   */
  private pendingRefresh: Observable<RefreshOutcome> | null = null;

  login(credentials: CustomerCredentials): Observable<void> {
    return this.http.post<unknown>(`${CUSTOMER_AUTH_URL_PREFIX}login`, credentials).pipe(
      map((body) => {
        const csrfToken = readCsrfToken(body);

        if (csrfToken === null) {
          throw new Error('Odpowiedź logowania nie zawiera tokenu CSRF.');
        }

        // BEFORE the session flips to `authenticated`: whoever reacts to that
        // signal must not be able to read the previous account out of a cache.
        this.resetIdentityCaches();
        this.acceptSession(csrfToken);
      }),
      catchError((error: unknown) => {
        // Same split as `refresh()`: only a 401/403 is the backend's verdict on
        // credentials, and a rejected login leaves nothing behind. The reason
        // stays with the backend — it answers a byte-identical 401 for every
        // failure, so the caller renders one generic message and never guesses
        // further. A 5xx or a transport error says nothing about identity, so a
        // session that may still be alive is not torn down over an outage.
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          this.dropSession();
        }

        throw error;
      }),
    );
  }

  logout(): Observable<void> {
    return this.http.post<unknown>(`${CUSTOMER_AUTH_URL_PREFIX}logout`, {}).pipe(
      // Deliberate catch-all, against the usual rule of classifying I/O failures:
      // logout is idempotent server-side and the only local outcome that makes
      // sense is "session gone". Keeping the customer marked as logged in because
      // the network blipped would be strictly worse than dropping the state.
      catchError(() => of(null)),
      map(() => {
        this.dropSession();
      }),
    );
  }

  /**
   * Silent refresh. Emits one `RefreshOutcome` — the four cases are enumerated, and
   * every caller decides each of them explicitly:
   *
   *  · `'authenticated'` — the backend rotated the CSRF token and the session lives;
   *  · `'rejected'` — 401 (refresh cookie missing or dead) or 403 (kept as a
   *    defensive case). These are the ONLY statuses that are the server's verdict on
   *    identity, so these are the only ones that drop the session;
   *  · `'throttled'` — 429, the per-IP rate limit on `/refresh`. It says nothing
   *    about identity: the backend's guard answers BEFORE the handler, so no cookie
   *    is touched. The panel REMEMBERS it (`isRefreshThrottled`) and sends no
   *    further `/refresh` for `REFRESH_THROTTLE_COOLDOWN_MS`;
   *  · `'unavailable'` — everything else, including 400/404, 5xx, a transport error
   *    and a malformed body. Infrastructure, not an answer about identity, so the
   *    state we had is kept and an outage logs nobody out.
   *
   * The status is the only discriminator used: 429 and 401 carry a byte-identical
   * body shape, so `message` is never parsed. Nor is any response header read —
   * the API is cross-origin and `enableCors` exposes none, so `Retry-After` never
   * reaches the browser and the backoff above is self-contained.
   *
   * `/customer-auth/refresh` is the one customer endpoint without the CSRF
   * double-submit check, precisely so this call can run after a page reload: the
   * token lives only in `CsrfTokenStore` (in memory), so a reloaded tab has none
   * to send. The backend authenticates the call on the HttpOnly, SameSite=Strict
   * refresh cookie alone, which is why a cold start can recover a live session.
   */
  refresh(): Observable<RefreshOutcome> {
    // Cooldown before the single-flight handle on purpose: this stream must not
    // become `pendingRefresh` nor run `finalize`, or it would clear the handle of
    // a request that is still in flight.
    if (this.refreshThrottled()) {
      return of<RefreshOutcome>('throttled');
    }

    const pending = this.pendingRefresh;

    if (pending !== null) {
      return pending;
    }

    const attempt = this.http.post<unknown>(`${CUSTOMER_AUTH_URL_PREFIX}refresh`, {}).pipe(
      map((body): RefreshOutcome => {
        const csrfToken = readCsrfToken(body);

        if (csrfToken === null) {
          // A malformed body is a broken contract, not a rejected session —
          // report the failure without deciding the customer is anonymous.
          return 'unavailable';
        }

        this.acceptSession(csrfToken);

        return 'authenticated';
      }),
      catchError((error: unknown) => {
        const outcome: RefreshOutcome =
          error instanceof HttpErrorResponse
            ? classifyRefreshOutcome(error.status)
            : 'unavailable';

        if (outcome === 'rejected') {
          this.dropSession();
        } else if (outcome === 'throttled') {
          this.startThrottleCooldown();
        }

        return of(outcome);
      }),
      finalize(() => {
        this.pendingRefresh = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.pendingRefresh = attempt;

    return attempt;
  }

  /**
   * Timer, not a `Date.now()` comparison: a `computed` reading the clock never
   * recomputes as time passes, so the login screen would keep showing a stale note
   * until the next navigation.
   */
  private startThrottleCooldown(): void {
    this.clearThrottleCooldown();
    this.refreshThrottled.set(true);
    this.throttleTimer = setTimeout(() => {
      this.throttleTimer = null;
      this.refreshThrottled.set(false);
    }, REFRESH_THROTTLE_COOLDOWN_MS);
  }

  private clearThrottleCooldown(): void {
    if (this.throttleTimer !== null) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }

    this.refreshThrottled.set(false);
  }

  /**
   * Per-account caches are emptied on a successful login, and on
   * every path that drops the session (S-10 impl-review F1, then S-15 phase 3).
   * Logout is not the only way a second account reaches the same SPA instance:
   * an expired session
   * bounces off `customerAuthGuard` onto `/logowanie`, and a customer may simply
   * navigate there without logging out — both leave the previous profile in the
   * caches, and the login navigation happens WITHOUT a page reload. Without this
   * reset, `establishmentOnlyGuard` would decide the NEW account's access on the OLD
   * account's `customerType`, the sidenav would offer the old account's sections,
   * and the closed-course-request form would prefill the previous establishment's
   * PII (name, address, phones, contact person, NIPs). Shared office machines make
   * that the normal case, not the exotic one.
   *
   * The drop half is enforced in `dropSession()` rather than in a component, because
   * that method is the single point all three roads run through: a 401/403 answer to
   * `login()`, `logout()`, and a `'rejected'` verdict from `refresh()`. What forced
   * the move is a consumer with no layout behind it — the account-deletion screen
   * (S-15) calls `logout()` on this service itself, so a reset living in
   * `PanelLayoutComponent` would simply never run for it.
   *
   * THREE in-memory carriers die there, not four, and the fourth is a decision rather
   * than an oversight: the CSRF token, the profile cache and the account-type signal
   * go, while the `empiria.signup.draft` in sessionStorage STAYS. That draft exists
   * exactly to survive the 401 → login → back cycle (Q-61/A,
   * `features/course-signup/signup-draft.ts`), and that 401 travels through
   * `dropSession()` — clearing it here would delete it in the one moment it was
   * written for. Only the account-deletion path clears it (S-15 phase 4).
   *
   * Deliberately NOT in `acceptSession()`: `refresh()` goes through that method
   * too, and a silent refresh describes the SAME account — resetting there
   * would buy nothing and cost one `/my/profile` per refresh.
   *
   * Cost: one profile request after each login, i.e. exactly what a freshly
   * logged-in account has to pay anyway.
   */
  private resetIdentityCaches(): void {
    this.myProfile.reset();
    this.customerProfile.reset();
  }

  private acceptSession(csrfToken: string): void {
    // The backend counts `/login` on a separate budget from `/refresh`, so a
    // customer who logged in normally after a 429 must not keep the cooldown.
    this.clearThrottleCooldown();
    this.csrfTokenStore.set(csrfToken);
    this.currentStatus.set('authenticated');
  }

  private dropSession(): void {
    this.clearThrottleCooldown();
    this.csrfTokenStore.clear();
    this.resetIdentityCaches();
    this.currentStatus.set('anonymous');
  }
}
